// Phase I0.3 — LegRow primitive.
// index + selection + odds + confidence dot + category chip + layer
// label + correlation pointer. The optional `reasoning` prop is piped
// through summarizeReasoning() so the Phase B forbidden-vocab guard
// applies to every reasoning surface that uses this primitive.

import type { CSSProperties } from "react";
import { summarizeReasoning } from "../../agents/reasoning";
import { layerTone, type LayerId } from "../../super-acca/categories";
import { Badge } from "./Badge";
import { CategoryChip } from "./CategoryChip";

export interface LegRowProps {
  /** 1-based leg index, displayed as "L01", "L02", etc. */
  index: number;
  market: string;
  selection: string;
  odds: number;
  /** 0–100. Drives the confidence dot colour. */
  confidence?: number | null;
  /** Category id (case-insensitive). */
  category?: string;
  /** Layer id (case-insensitive). */
  layer?: string;
  /** Other leg indices this leg is positively correlated with. */
  correlatedWith?: readonly number[];
  /** Optional reasoning text — automatically routed through summarizeReasoning. */
  reasoning?: string | null;
  /** Optional flag badge — e.g. "FLAGGED". */
  flagged?: boolean;
  /** Render anchor marker on the index. */
  anchor?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
}

function confColor(c: number | null | undefined): string {
  if (c == null || !Number.isFinite(c)) return "var(--sa-fg-muted)";
  if (c >= 70) return "var(--sa-signal-green)";
  if (c >= 50) return "var(--sa-accent-saffron)";
  return "var(--sa-signal-red)";
}

export function LegRow({
  index,
  market,
  selection,
  odds,
  confidence,
  category,
  layer,
  correlatedWith,
  reasoning,
  flagged,
  anchor,
  onClick,
  style,
}: LegRowProps) {
  const layerUpper = layer ? (layer.toUpperCase() as LayerId) : null;
  const tone = layerUpper ? layerTone(layerUpper) : "var(--sa-fg-muted)";
  const safeReasoning = reasoning ? summarizeReasoning(reasoning) : null;
  const hasCorr = correlatedWith && correlatedWith.length > 0;

  return (
    <div
      onClick={onClick}
      style={{
        padding: "14px 18px",
        borderTop: "1px solid var(--sa-hairline)",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            color: anchor ? "var(--sa-accent-saffron)" : "var(--sa-fg-muted)",
            letterSpacing: "0.16em",
            minWidth: 36,
          }}
        >
          {anchor ? "★" : ""}L{String(index).padStart(2, "0")}
        </span>
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: confColor(confidence ?? null),
          }}
        />
        {category ? <CategoryChip category={category} short /> : null}
        {layerUpper ? (
          <span
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              letterSpacing: "0.18em",
              color: tone,
              textTransform: "uppercase",
            }}
          >
            {layerUpper === "BYO" ? "BYO" : layerUpper}
          </span>
        ) : null}
        <div style={{ flex: 1, minWidth: 160 }}>
          <div
            style={{
              fontFamily: "var(--sa-font-body)",
              fontSize: 13.5,
              color: "var(--sa-fg-primary)",
            }}
          >
            {market} ·{" "}
            <span style={{ color: "var(--sa-accent-saffron)" }}>{selection}</span>
          </div>
          {hasCorr ? (
            <div
              style={{
                fontFamily: "var(--sa-font-mono)",
                fontSize: 10,
                color: "var(--sa-fg-muted)",
                marginTop: 4,
                letterSpacing: "0.14em",
              }}
            >
              ⇆ CORR{" "}
              {correlatedWith!.map((n) => `L${String(n).padStart(2, "0")}`).join(" · ")}
            </div>
          ) : null}
        </div>
        {flagged ? <Badge variant="warn">⚠ FLAGGED</Badge> : null}
        <span
          style={{
            fontFamily: "var(--sa-font-stat)",
            fontSize: 22,
            color: "var(--sa-fg-primary)",
            lineHeight: 1,
          }}
        >
          {odds.toFixed(2)}
          <span style={{ fontSize: 11, color: "var(--sa-fg-muted)" }}>×</span>
        </span>
      </div>
      {safeReasoning ? (
        <p
          style={{
            margin: "10px 0 0 48px",
            fontFamily: "var(--sa-font-body)",
            fontSize: 13,
            color: "var(--sa-fg-secondary)",
            lineHeight: 1.6,
          }}
        >
          {safeReasoning}
        </p>
      ) : null}
    </div>
  );
}
